import type { ParsedMetadataSummary, StructuredModelContext } from './modelContextTypes';

export const SUGGESTED_QUESTION_TEMPLATES = [
    'What is this model used for, and who should query it?',
    'What is the grain of the primary entity, and how should agents join related tables?',
    'What should agents never do with this data?',
    'Give 4 example questions this data model should answer as a tool.',
    'Define the key business terms and domain for this model.',
] as const;

export const SYNTHETIC_INTENDED_USE = [
    'Answer operational and analytical questions as an agent tool',
    'Ground LLM responses in the attached schema and query templates',
    'Prefer joins and filters that match documented grain and keys',
];

export const SYNTHETIC_USAGE_GUIDELINES = [
    'Always filter by tenant, customer, or time window when those fields exist.',
    'Use documented primary and foreign keys; do not invent join paths.',
    'Return only columns needed for the question; avoid SELECT * in production tools.',
    'If a question cannot be answered from attached entities, say so instead of guessing.',
];

export const SYNTHETIC_CONSTRAINTS = [
    'Do not expose secrets, passwords, tokens, or raw PII beyond what the question requires.',
    'Do not mutate data; this model is read-oriented unless a query template explicitly writes.',
    'Do not cross-join large fact tables without a key predicate.',
    'Respect nullable keys: treat missing foreign keys as unmatched, not as errors.',
];

export function defaultBusinessDomain(summary: ParsedMetadataSummary, sourceType: string): string {
    const ids = summary.entities.map((e) => `${e.id} ${e.displayName}`.toLowerCase()).join(' ');
    if (/\b(order|sales|invoice|payment|product)\b/.test(ids)) return 'Revenue operations (orders, products, payments)';
    if (/\b(customer|account|contact|crm)\b/.test(ids)) return 'Customer / CRM';
    if (/\b(employee|hr|payroll|department)\b/.test(ids)) return 'Human resources';
    if (summary.isCustomQuery) return `Custom query over ${sourceType || 'the attached source'}`;
    if (sourceType) return `Operational data for ${sourceType}`;
    return 'Enterprise operational data';
}

export function defaultPurpose(modelName: string, summary: ParsedMetadataSummary): string {
    const names = summary.entities.slice(0, 4).map((e) => e.displayName).join(', ');
    if (summary.isCustomQuery) {
        return `${modelName || 'This custom query'} is a reusable tool query. Agents should treat the result set as the contract: only columns in the template are available.`;
    }
    if (names) {
        return `${modelName || 'This data model'} exposes ${names}${summary.entities.length > 4 ? ', and related entities' : ''} so agents can answer factual questions with schema-backed tools.`;
    }
    return `${modelName || 'This data model'} provides structured source metadata for later agent tool use.`;
}

export function defaultGlossary(summary: ParsedMetadataSummary): StructuredModelContext['glossary'] {
    const terms = summary.entities.slice(0, 6).map((entity) => {
        const keys = entity.fields.filter((f) => f.isPrimaryKey).map((f) => f.name);
        const keyHint = keys.length ? ` Key: ${keys.join(', ')}.` : '';
        return {
            term: entity.displayName,
            definition: `${entity.displayName} (${entity.id}) is a ${entity.entityType || 'entity'} in this model.${keyHint}`,
        };
    });
    if (terms.length === 0) {
        return [
            { term: 'Grain', definition: 'The lowest level of detail a row represents (for example one order, one line item, or one daily snapshot).' },
            { term: 'Tool contract', definition: 'The metadata and additional context an LLM should follow when calling this model as a tool.' },
        ];
    }
    return terms;
}

export function defaultExampleQuestions(summary: ParsedMetadataSummary): string[] {
    const primary = summary.entities[0];
    const secondary = summary.entities[1];
    if (!primary) {
        return [
            'What entities and fields are available in this model?',
            'Which filters should I apply before aggregating?',
            'What is the intended grain of a typical query?',
            'When should I refuse to answer from this model?',
        ];
    }
    const examples = [
        `How many ${primary.displayName.toLowerCase()} records exist in the latest period?`,
        `Show the key fields for ${primary.displayName} and how they uniquely identify a row.`,
    ];
    if (secondary) {
        examples.push(`How does ${primary.displayName} relate to ${secondary.displayName}, and which keys join them?`);
    } else {
        examples.push(`What filters are required when querying ${primary.displayName}?`);
    }
    examples.push(`Which questions should this model not answer?`);
    return examples;
}

export function packLlmInstructions(ctx: StructuredModelContext): string {
    return [
        ctx.contextNarrative?.trim()
            ? ctx.contextNarrative.trim()
            : `You are using the data model "${ctx.modelName}" as a tool (source type: ${ctx.sourceType || 'unknown'}).`,
        ctx.sourceMetadata?.trim() ? `Source metadata JSON:\n${ctx.sourceMetadata}` : '',
        'If the user question cannot be answered from this contract, say so. Do not invent tables, columns, files, or fields.',
    ]
        .filter(Boolean)
        .join('\n\n');
}
